/**
 * ModuleSettingsPage — F12.D T12.D.10.
 *
 * Hub konfiguracyjny per moduł — 12 zakładek à la SellCall.
 * Route: `/modules/:id/settings` (tenant scope).
 *
 * Zakładki kontentne (mamy implementację):
 *  - typy   → CategorySettingsPage embedded
 *  - layout → ModuleBuilderPage embedded
 *  - validation → placeholder z linkiem do buildera (validation żyje w buildery prawym panelu, T12.D.10b)
 *
 * Zakładki placeholder (Wkrótce):
 *  - columns / widgets / filters / buttons / kanban / page / automations / access / numbering
 *  - każdy ma label fazy F12.X w której się pojawi
 *
 * Deep linking: `?tab=<id>` (BC z istniejącymi route'ami /builder, /categories przez redirect w App.tsx).
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  BarChart3,
  ClipboardList,
  Columns,
  Filter,
  Hash,
  Layout,
  LayoutGrid,
  type LucideIcon,
  MousePointer2,
  Settings2,
  Shield,
  Tag,
  Trello,
  Zap,
} from 'lucide-react';

import { ListKanbanPanel } from '@/components/configurable-table';
import type { ModuleField } from '@/components/fields/FieldRegistry';
import { ActionButtonConfigurator } from '@/components/modules/ActionButtonConfigurator';
import { AutomationsPanel } from '@/components/modules/AutomationsPanel';
import { renderIconByName } from '@/components/modules/IconPicker';
import { ModuleAccessPanel } from '@/components/modules/ModuleAccessPanel';
import { ModuleAuditLog } from '@/components/modules/ModuleAuditLog';
import { NumberingConfigPanel } from '@/components/modules/NumberingConfigPanel';
import { PageConfigPanel } from '@/components/modules/PageConfigPanel';
import { SavedViewsSettingsPanel } from '@/components/modules/SavedViewsManager';
import { TabPlaceholder } from '@/components/modules/TabPlaceholder';
import { Spinner } from '@/components/Spinner';
import { useTabLabel } from '@/components/tabs/useTabLabel';
import { api } from '@/lib/api';

import { CategorySettingsPage } from './CategorySettingsPage';
import { ModuleBuilderPage } from './ModuleBuilderPage';

interface ModuleInfo {
  id: string;
  name: string;
  slug: string;
  icon: string;
  color: string;
  fields?: Array<{
    id: string;
    slug: string;
    name: string;
    fieldType: string;
    isRequired: boolean;
    isUnique: boolean;
    showInList: boolean;
    sortOrder: number;
    options?: unknown[];
  }>;
}

type TabId =
  | 'types'
  | 'columns'
  | 'layout'
  | 'widgets'
  | 'filters'
  | 'buttons'
  | 'kanban'
  | 'page'
  | 'validation'
  | 'automations'
  | 'access'
  | 'numbering'
  | 'audit';

const VALID_TABS: TabId[] = [
  'types',
  'columns',
  'layout',
  'widgets',
  'filters',
  'buttons',
  'kanban',
  'page',
  'validation',
  'automations',
  'access',
  'numbering',
  'audit',
];

interface TabDef {
  id: TabId;
  labelKey: string;
  icon: LucideIcon;
}

const TABS: TabDef[] = [
  { id: 'types', labelKey: 'tenant.moduleSettings.tabs.types', icon: Tag },
  { id: 'columns', labelKey: 'tenant.moduleSettings.tabs.columns', icon: Columns },
  { id: 'layout', labelKey: 'tenant.moduleSettings.tabs.layout', icon: Layout },
  { id: 'widgets', labelKey: 'tenant.moduleSettings.tabs.widgets', icon: LayoutGrid },
  { id: 'filters', labelKey: 'tenant.moduleSettings.tabs.filters', icon: Filter },
  { id: 'buttons', labelKey: 'tenant.moduleSettings.tabs.buttons', icon: MousePointer2 },
  { id: 'kanban', labelKey: 'tenant.moduleSettings.tabs.kanban', icon: Trello },
  { id: 'page', labelKey: 'tenant.moduleSettings.tabs.page', icon: Settings2 },
  { id: 'validation', labelKey: 'tenant.moduleSettings.tabs.validation', icon: AlertTriangle },
  { id: 'automations', labelKey: 'tenant.moduleSettings.tabs.automations', icon: Zap },
  { id: 'access', labelKey: 'tenant.moduleSettings.tabs.access', icon: Shield },
  { id: 'numbering', labelKey: 'tenant.moduleSettings.tabs.numbering', icon: Hash },
  { id: 'audit', labelKey: 'tenant.moduleSettings.tabs.audit', icon: ClipboardList },
];

function isValidTab(value: string | null): value is TabId {
  return !!value && (VALID_TABS as string[]).includes(value);
}

export function ModuleSettingsPage() {
  const { id: moduleId } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const requested = params.get('tab');
  const activeTab: TabId = isValidTab(requested) ? requested : 'types';

  const moduleQuery = useQuery({
    queryKey: ['module', moduleId, 'detail'],
    queryFn: async () => {
      const res = await api.get<{ module: ModuleInfo }>(`/modules/${moduleId}`);
      return res.data.module;
    },
    enabled: !!moduleId,
  });
  useTabLabel(
    moduleQuery.data ? t('tenant.moduleSettings.title', { name: moduleQuery.data.name }) : null,
  );

  const setTab = (next: TabId) => {
    const p = new URLSearchParams(params);
    p.set('tab', next);
    setParams(p, { replace: true });
  };

  const tabContent = useMemo(() => {
    if (!moduleId) return null;

    switch (activeTab) {
      case 'types':
        return <CategorySettingsPage embedded moduleId={moduleId} />;

      case 'layout':
        return <ModuleBuilderPage embedded moduleId={moduleId} />;

      case 'validation':
        return (
          <TabPlaceholder
            title={t('tenant.moduleSettings.tabs.validation')}
            description={t('tenant.moduleSettings.placeholders.validation.description')}
            phase="F12.D.10b"
            icon={AlertTriangle}
            fallbackHint={t('tenant.moduleSettings.placeholders.validation.hint')}
          />
        );

      case 'columns':
        return (
          <TabPlaceholder
            title={t('tenant.moduleSettings.tabs.columns')}
            description={t('tenant.moduleSettings.placeholders.columns.description')}
            phase="F12.E.9"
            icon={Columns}
          />
        );

      case 'widgets':
        return (
          <TabPlaceholder
            title={t('tenant.moduleSettings.tabs.widgets')}
            description={t('tenant.moduleSettings.placeholders.widgets.description')}
            phase="F12.H.1"
            icon={BarChart3}
          />
        );

      case 'filters':
        return <SavedViewsSettingsPanel moduleId={moduleId} />;

      case 'buttons':
        return (
          <ActionButtonConfigurator
            moduleId={moduleId}
            fields={(moduleQuery.data?.fields ?? []) as unknown as ModuleField[]}
          />
        );

      case 'kanban':
        return <ListKanbanPanel slug={`module-${moduleId}`} />;

      case 'page':
        return <PageConfigPanel moduleId={moduleId} />;

      case 'automations':
        return (
          <AutomationsPanel
            basePath={`/modules/${moduleId}/automations`}
            fields={(moduleQuery.data?.fields ?? []) as unknown as ModuleField[]}
            moduleCode={moduleQuery.data?.slug ? `custom.${moduleQuery.data.slug}` : undefined}
          />
        );

      case 'access':
        return <ModuleAccessPanel moduleId={moduleId} moduleSlug={moduleQuery.data?.slug ?? ''} />;

      case 'numbering':
        return <NumberingConfigPanel moduleId={moduleId} />;

      case 'audit':
        return <ModuleAuditLog moduleId={moduleId} />;

      default:
        return null;
    }
  }, [activeTab, moduleId, t, moduleQuery.data?.fields, moduleQuery.data?.slug]);

  if (!moduleId) {
    return <main className="p-6 text-error-500">Brak ID modułu</main>;
  }

  if (moduleQuery.isLoading) {
    return (
      <main className="flex min-h-full items-center justify-center bg-bg-secondary p-6">
        <Spinner />
      </main>
    );
  }

  const module = moduleQuery.data;

  return (
    <main className="min-h-full bg-bg-secondary" data-testid="module-settings-page">
      {/* Header z nawigacją wstecz + meta modułu */}
      <header className="border-b border-border-primary bg-bg-primary px-6 py-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/modules')}
            aria-label={t('common.back')}
            className="rounded-md p-1 text-text-muted hover:bg-bg-tertiary"
            data-testid="module-settings-back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          {module && (
            <>
              <div
                className="flex h-10 w-10 items-center justify-center rounded-lg"
                style={{ backgroundColor: `${module.color}20`, color: module.color }}
                aria-hidden="true"
              >
                {renderIconByName(module.icon, 'h-5 w-5')}
              </div>
              <div className="min-w-0">
                <h1 className="truncate text-xl font-bold text-text-primary">
                  {t('tenant.moduleSettings.title', { name: module.name })}
                </h1>
                <p className="text-xs text-text-tertiary">{t('tenant.moduleSettings.subtitle')}</p>
              </div>
            </>
          )}
        </div>
      </header>

      {/* Tab bar */}
      <div className="overflow-x-auto border-b border-border-primary bg-bg-primary">
        <nav
          className="flex min-w-max"
          aria-label={t('tenant.moduleSettings.title')}
          data-testid="module-settings-tabs"
        >
          {TABS.map((tab) => {
            const isActive = tab.id === activeTab;
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setTab(tab.id)}
                className={
                  '-mb-px flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-3 text-sm transition-colors ' +
                  (isActive
                    ? 'border-accent-500 font-medium text-accent-600'
                    : 'border-transparent text-text-secondary hover:text-text-primary')
                }
                data-testid={`module-settings-tab-${tab.id}`}
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                {t(tab.labelKey)}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab content */}
      <div className="p-6" data-testid="module-settings-content">
        {tabContent}
      </div>
    </main>
  );
}
